import React from "react";
import { format } from "date-fns";

import { Chip } from "@mui/material";

import DataTable from "../../../common/DataTable";
import ColumnHeader from "../../../common/ColumnHeader";

import { getChipColor } from "../../../../constants/value";
import { dateSortComparator } from "../../../../utils/date";
import { formatPwgBalance } from "../../../../utils/adjustBalance";

const SimInfoList = ({
  data,
  meta,
  client,
  loading,
  handleSort,
  disableRowClick,
  handlePageChange,
  handleColumnClick,
  handleFilterChange,
  handlePageSizeChange,
  ...props
}) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    {
      field: "iccid",
      headerName: "ICCID",
      minWidth: 200,
      flex: 3,
      sortable: false,
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"ICCID"}
            handleClick={() => handleColumnClick("iccid")}
          />
        ) : (
          "ICCID"
        ),
    },
    {
      field: "msisdn",
      headerName: "MDN",
      minWidth: 200,
      flex: 3,
      sortable: false,
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"MDN"}
            handleClick={() => handleColumnClick("msisdn")}
          />
        ) : (
          "MDN"
        ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: 2,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={getChipColor(params.row.status)}
        />
      ),
      sortable: false,
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"Status"}
            handleClick={() => handleColumnClick("status")}
          />
        ) : (
          "Status"
        ),
    },
    {
      field: "sim_status",
      headerName: "SIM Status",
      minWidth: 110,
      flex: 2,
      sortable: false,
      filterable: false,
      valueGetter: (params) =>
        params.row.extra_data?.sim_status ||
        params.row.extra_data?.iccid_status ||
        "",
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"SIM Status"}
            handleClick={() => handleColumnClick("extra_data.sim_status")}
          />
        ) : (
          "SIM Status"
        ),
    },
    {
      field: "ms_status",
      headerName: "Network Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.row.extra_data?.ms_status || "",
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"Network Status"}
            handleClick={() => handleColumnClick("extra_data.ms_status")}
          />
        ) : (
          "Network Status"
        ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 200,
      filterable: false,
      flex: 3,
      sortable: false,
      valueGetter: (params) => params.row.created_at || null,
      valueFormatter: (params) =>
        params.value &&
        `${format(new Date(params.value), "MMMM d, y hh:mm a")}`,
      sortComparator: dateSortComparator,
    },
    {
      field: "last_updated",
      headerName: "Last Updated",
      minWidth: 200,
      filterable: false,
      flex: 3,
      sortable: false,
      valueGetter: (params) => params.row.last_updated || null,
      valueFormatter: (params) =>
        params.value &&
        `${format(new Date(params.value), "MMMM d, y hh:mm a")}`,
      sortComparator: dateSortComparator,
    },
    {
      field: "qr_activation_code",
      headerName: "QR Activation Code",
      minWidth: 260,
      flex: 4,
      sortable: false,
      filterable: false,
      valueGetter: (params) =>
        params.row.extra_data?.esim_qr_activation_code || "",
    },
    {
      field: "plan_name",
      headerName: "Plan Name",
      minWidth: 160,
      flex: 3,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.row.extra_data?.plan_name || "",
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"Plan Name"}
            handleClick={() => handleColumnClick("extra_data.plan_name")}
          />
        ) : (
          "Plan Name"
        ),
    },
    {
      field: "plan_id",
      headerName: "Plan ID",
      minWidth: 100,
      flex: 2,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.row.extra_data?.plan_id || "",
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"Plan ID"}
            handleClick={() => handleColumnClick("extra_data.plan_id")}
          />
        ) : (
          "Plan ID"
        ),
    },
    {
      field: "subscription_id",
      headerName: "Subscription ID",
      minWidth: 200,
      flex: 3,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.row.extra_data?.subscription_id || "",
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"Subscription ID"}
            handleClick={() => handleColumnClick("extra_data.subscription_id")}
          />
        ) : (
          "Subscription ID"
        ),
    },
    {
      field: "talk_balance",
      headerName: "Talk Balance (minutes)",
      minWidth: 130,
      flex: 2,
      sortable: false,
      filterable: false,
      valueGetter: (params) =>
        formatPwgBalance("voice", params.row.extra_data?.TOTALBALANCE?.TALK),
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"Talk Balance (minutes)"}
            handleClick={() =>
              handleColumnClick("extra_data.TOTALBALANCE.TALK")
            }
          />
        ) : (
          "Talk Balance (minutes)"
        ),
    },
    {
      field: "text_balance",
      headerName: "Text Balance",
      minWidth: 130,
      flex: 2,
      sortable: false,
      filterable: false,
      valueGetter: (params) =>
        params.row.extra_data?.TOTALBALANCE?.TEXT ?? "",
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"Text Balance"}
            handleClick={() =>
              handleColumnClick("extra_data.TOTALBALANCE.TEXT")
            }
          />
        ) : (
          "Text Balance"
        ),
    },
    {
      field: "data_balance",
      headerName: "Data Balance (MB)",
      minWidth: 130,
      flex: 2,
      sortable: false,
      filterable: false,
      valueGetter: (params) =>
        formatPwgBalance("data", params.row.extra_data?.TOTALBALANCE?.DATA),
      renderHeader: (params) =>
        client ? (
          <ColumnHeader
            label={"Data Balance (MB)"}
            handleClick={() =>
              handleColumnClick("extra_data.TOTALBALANCE.DATA")
            }
          />
        ) : (
          "Data Balance (MB)"
        ),
    },
  ];

  return (
    <DataTable
      disableColumnFilter
      loading={loading}
      data={data}
      client={client}
      columns={
        client ? columns.map((val) => ({ ...val, sortable: true })) : columns
      }
      metaData={meta}
      handleRowClick={null}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      {...props}
    />
  );
};

export default SimInfoList;
